import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Cake, X } from "lucide-react";
import {
    BIRTHDAY_REMINDER_DISMISS_STORAGE_KEY,
    BIRTHDAY_REMINDER_SHOW_AGAIN_WITHIN_DAYS,
    emptyDismissState,
    isBirthdayReminderItemVisible,
    parseDismissState,
    reminderBirthdayKey,
} from "../lib/birthdayReminderDismiss";

function daysWordUk(n) {
    const k = n % 10;
    const k100 = n % 100;
    if (k === 1 && k100 !== 11) return "день";
    if ([2, 3, 4].includes(k) && ![12, 13, 14].includes(k100)) return "дні";
    return "днів";
}

/**
 * Смуга під шапкою (не alert): нагадування про ДН за 1–14 днів для всіх, хто бачить графік.
 * Іменинник зі списку нагадувань смугу не бачить.
 */
export default function BirthdayReminderBar({ user }) {
    const [items, setItems] = useState([]);
    const [dismissState, setDismissState] = useState(() =>
        typeof localStorage !== "undefined"
            ? parseDismissState(localStorage.getItem(BIRTHDAY_REMINDER_DISMISS_STORAGE_KEY))
            : emptyDismissState()
    );

    useEffect(() => {
        if (!user || user.role === "targetolog") {
            setItems([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get("/api/schedule/birthday-reminders");
                if (!cancelled) setItems(Array.isArray(data?.items) ? data.items : []);
            } catch {
                if (!cancelled) setItems([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user]);

    useEffect(() => {
        if (typeof localStorage === "undefined") return;
        setDismissState(parseDismissState(localStorage.getItem(BIRTHDAY_REMINDER_DISMISS_STORAGE_KEY)));
    }, [user, items.length]);

    const myId =
        user?._id != null ? String(user._id) : user?.id != null ? String(user.id) : "";

    const visibleToMe = useMemo(() => {
        const notMe = items.filter((it) => String(it.userId) !== myId);
        return notMe.filter((it) => isBirthdayReminderItemVisible(it, dismissState));
    }, [items, myId, dismissState]);

    const dismissBar = useCallback(() => {
        if (!visibleToMe.length) return;
        const next = {
            far: { ...dismissState.far },
            near: { ...dismissState.near },
        };
        for (const it of visibleToMe) {
            const nb = reminderBirthdayKey(it);
            if (!nb) continue;
            const uid = String(it.userId);
            if (it.daysUntil <= BIRTHDAY_REMINDER_SHOW_AGAIN_WITHIN_DAYS) next.near[uid] = nb;
            else next.far[uid] = nb;
        }
        try {
            localStorage.setItem(BIRTHDAY_REMINDER_DISMISS_STORAGE_KEY, JSON.stringify(next));
        } catch {
            /* ignore */
        }
        setDismissState(next);
    }, [visibleToMe, dismissState]);

    if (!visibleToMe.length) return null;

    const text = visibleToMe
        .map(
            (it) =>
                `${it.displayName} — ${it.dateLabel} (за ${it.daysUntil} ${daysWordUk(it.daysUntil)})`
        )
        .join(" · ");

    return (
        <div className="app-birthday-reminder-bar" role="status">
            <Cake className="app-birthday-reminder-bar__icon flex-shrink-0" size={18} strokeWidth={2} aria-hidden />
            <span className="app-birthday-reminder-bar__label">Незабаром дні народження:</span>
            <span className="app-birthday-reminder-bar__text">{text}</span>
            <button
                type="button"
                className="app-birthday-reminder-bar__close"
                onClick={dismissBar}
                aria-label="Закрити нагадування"
                title={`Закрити. Якщо до дня народження більше ${BIRTHDAY_REMINDER_SHOW_AGAIN_WITHIN_DAYS} днів — з’явиться знову за ${BIRTHDAY_REMINDER_SHOW_AGAIN_WITHIN_DAYS} дні; якщо вже «фінальне» нагадування — до наступного року.`}
            >
                <X size={18} strokeWidth={2.25} aria-hidden />
            </button>
        </div>
    );
}
